// Client end-user interaction capture (feeds Studio → Searches).
//
// A module singleton so any component can emit without prop-threading. Every
// entry point NO-OPS unless capture is enabled (genuine end-users only — set by
// Layout via configureTracking) AND, for destinations, a trigger window is open.
// Failures are swallowed: this is best-effort telemetry and must never disrupt
// the end-user's experience.
//
// v1 captures the FIRST destination after a trigger. MAX_STEPS reserves room for
// the eventual 3 (the model + the Studio table already carry 3 columns; the
// not-yet-captured ones render greyed).
import { interactionTraces, type TracePage, type TracePattern, type TraceStep } from '../api'

const MAX_STEPS = 1
const IDLE_MS = 90_000

type OpenTrace = {
  page: TracePage
  pattern: TracePattern
  trigger: Record<string, unknown>
  steps: TraceStep[]
}

let enabled = false
let current: OpenTrace | null = null
let idleTimer: ReturnType<typeof setTimeout> | null = null

function clearIdle() {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null }
}
function armIdle() {
  clearIdle()
  idleTimer = setTimeout(() => finalizeCurrent(), IDLE_MS)
}

function post(t: OpenTrace) {
  interactionTraces
    .create({ page: t.page, pattern: t.pattern, trigger: t.trigger, steps: t.steps })
    .catch(() => { /* best-effort telemetry; never surface to the user */ })
}

/** Close the open trace, posting it unless it's an empty search the user cleared
 *  without ever committing. A trigger with zero follow-up clicks IS recorded —
 *  a search that led nowhere is itself signal. */
export function finalizeCurrent() {
  clearIdle()
  const t = current
  current = null
  if (!t) return
  if (t.pattern === 'top_left' && !String(t.trigger.text ?? '').trim() && t.steps.length === 0) return
  post(t)
}

/** Called by Layout from auth: true only for genuine client end-users
 *  (not superusers, not studio roles). Flipping off drops any open trace. */
export function configureTracking(next: boolean) {
  if (next === enabled) return
  enabled = next
  if (!enabled) { clearIdle(); current = null }
}

/** Main pane-1 search box. Coalesces keystrokes into one open 'top_left' trace
 *  (updated in place) until a destination click, a new trigger, nav-away, or
 *  idle finalizes it. Clearing the box before clicking discards the trace. */
export function traceSearch(text: string) {
  if (!enabled) return
  const clean = text || ''
  if (current && current.pattern === 'top_left' && current.steps.length === 0) {
    if (!clean.trim()) { clearIdle(); current = null; return }
    current.trigger = { kind: 'search', text: clean }
    armIdle()
    return
  }
  if (!clean.trim()) return
  finalizeCurrent()
  current = { page: 'main', pattern: 'top_left', trigger: { kind: 'search', text: clean }, steps: [] }
  armIdle()
}

/** Main pane-1 sort controls: control ∈ {dial, clock, collapse, expand, alpha};
 *  value is the control's setting where meaningful (e.g. the dial window). */
export function traceSort(control: string, value?: string) {
  if (!enabled) return
  finalizeCurrent()
  const trigger: Record<string, unknown> = { kind: 'sort', control }
  if (value != null) trigger.value = value
  current = { page: 'main', pattern: 'sort', trigger, steps: [] }
  armIdle()
}

/** Stats / Playbook / Privacy page visit — a standalone row with no follow-up steps. */
export function traceVisit(page: TracePage) {
  if (!enabled) return
  finalizeCurrent()
  post({ page, pattern: 'visit', trigger: { kind: 'visit' }, steps: [] })
}

/** An end-user wall creation (Privacy page or pane-3 Wall button). Standalone
 *  row on the Privacy page; carries the wall's match/mode + where it was made.
 *  Ken 2026-08-23. */
export function traceWallCreate(detail: { match?: string; mode?: string; source?: string }) {
  if (!enabled) return
  finalizeCurrent()
  post({ page: 'privacy', pattern: 'wall_create', trigger: { kind: 'wall_create', ...detail }, steps: [] })
}

/** An end-user re-file: an email moved from one matter into another (pane-3
 *  Refile). Standalone row on the Search page; carries the email + from/to
 *  matter so the correction is auditable. Ken 2026-10-19. */
export function traceRefile(detail: {
  artifact_id: number
  from_matter_id: number | null
  to_matter_id: number | null
  to_matter_was_new?: boolean
}) {
  if (!enabled) return
  finalizeCurrent()
  post({ page: 'main', pattern: 'refile', trigger: { kind: 'refile', ...detail }, steps: [] })
}

/** A follow-up navigation click (folder / matter / card). No-op unless a trigger
 *  window is open. Fills up to MAX_STEPS, then finalizes. */
export function traceDestination(step: TraceStep) {
  if (!enabled || !current) return
  if (current.steps.length >= MAX_STEPS) return
  current.steps.push({ ...step, at: new Date().toISOString() })
  if (current.steps.length >= MAX_STEPS) finalizeCurrent()
  else armIdle()
}
